import printJS from "print-js";
import React, { useMemo, useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import {
  BsChevronDown,
  BsChevronUp,
  BsPrinter,
  BsTrash3,
} from "react-icons/bs";
import { FaSearchPlus } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { Box, Flex } from "rebass";
import styled from "styled-components";
import useResponsive from "../../hooks/useResponsive";
import B2b from "../../icons/B2b";
import Bin from "../../icons/Bin";
import Plus from "../../icons/Plus";
import Print from "../../icons/Print";
import Search from "../../icons/Search";
import SearchPlus from "../../icons/SearchPlus";
import { yodaOrdersActions } from "../../logic/actions";
import { yodaOrdersService } from "../../logic/services";
import { ORDER_STATUS } from "../../model/order";
import { getLocalizedString, theme } from "../../utils";
import getOrderStatus from "../../utils/getOrderStatus";
import config from "../../utils/globalConfig";
import { Price } from "../common/Price";
import Tooltip from "../common/Tooltip";
import { ToastMessage } from "./ToastMessage";

const OrderDetailText = styled.strong`
  display: inline-block;
  margin-bottom: 15px;
`;

const OrderDraft = ({
  id,
  quantityReorder,
  quantitySwap,
  orderDateFe,
  orderNumber,
  type,
  typeId,
  status,
  statusDescription,
  season,
  lineDescription,
  agentId,
  customerId,
  totalReorder,
  totalSwap,
  b2B,
  currencyCode,
  customerCode,
  customerName,
  recycled,
  customData,
}) => {
  const lang = localStorage.getItem("B2BLanguage") === "ITA" ? "it" : "en";

  const {
    color: statusColor,
    label: statusLabel,
    status: orderStatus,
  } = getOrderStatus({ status, statusDescription, recycled }, "STATUS");

  const isB2b = b2B;
  const [hoverEffect, setHoverEffect] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState();
  const [showExpanded, setShowExpanded] = useState(false);

  const dispatch = useDispatch();
  const navigate = useHistory();
  const orderType = config.ORDER_TYPE_REORDER;

  const { isMobile, isTabletSm } = useResponsive();

  const print = async () => {
    dispatch(yodaOrdersActions.loading(true));
    const report = await yodaOrdersService.generateReport(
      String(id),
      localStorage.getItem("B2BLanguage") === "ITA" ? "IT" : "EN",
    );
    const res = await blobToBase64Async(report);
    dispatch(yodaOrdersActions.loading(false));
    printJS({ printable: res, type: "pdf", base64: true });
  };

  async function blobToBase64Async(blob) {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onerror = (e) => reject(fileReader.error);
      fileReader.onloadend = (e) => {
        const dataUrl = fileReader.result;
        const base64 = dataUrl.substring(dataUrl.indexOf(",") + 1);
        resolve(base64);
      };
      fileReader.readAsDataURL(blob);
    });
  }

  const totalQuantity = useMemo(() => {
    const total = quantityReorder + quantitySwap;
    return total ? total : "0";
  }, [quantityReorder, quantitySwap]);

  const isDisabled = orderStatus === ORDER_STATUS.CANCELLED;

  return !isMobile && !isTabletSm ? (
    <Flex
      width={1}
      flexDirection="column"
      mb={20}
      onMouseEnter={() => {
        if (!isDisabled) setHoverEffect(true);
      }}
      onMouseLeave={() => {
        if (!isDisabled) setHoverEffect(false);
      }}
      sx={{
        cursor: "pointer",
        ...(isDisabled && {
          cursor: "unset",
        }),
      }}
    >
      <ToastMessage
        message={deleteMessage}
        needConfirm={true}
        confirmFunction={() => {
          dispatch(yodaOrdersActions.removeOrder(id));
          setDeleteMessage(null);
        }}
        cancelFunction={() => {
          setDeleteMessage(null);
        }}
        style={{ width: 600 }}
      />

      <Flex width={1} flexDirection={["column", null, "row"]} p={[2, null, 0]}>
        <Flex
          flexDirection={["row", null, "column"]}
          backgroundColor={statusColor}
          justifyContent={"space-between"}
          p={2}
          mb={[2, null, 0]}
          mr={[0, null, 2]}
        ></Flex>

        <Link
          onClick={(e) => {
            e.stopPropagation();
            if (customerId) {
              localStorage.setItem("selectedCustomer", customerId);
            }
            if (agentId) {
              localStorage.setItem("selectedAgent", agentId);
            }
          }}
          style={{
            position: "relative",
            width: "100%",
            ...(isDisabled && {
              pointerEvents: "none",
            }),
          }}
          to={
            orderStatus == ORDER_STATUS.CONFIRMED
              ? "/orderReport/" + id
              : "/orderDetail/" + id
          }
        >
          <Flex
            width={1}
            backgroundColor={hoverEffect ? "#f8f8f8" : "#f2f2f2"}
            p={3}
            sx={{
              color: "#232323",
            }}
            flexDirection={["column", null, "row"]}
          >
            <Flex width={1} flexDirection="column" justifyContent="center">
              <Tooltip
                content={
                  isB2b ? getLocalizedString("ORDER_CREATED_IN_B2B") : null
                }
              >
                <Box mb={3} ml={"-7px"} mt={1} width="fit-content">
                  {isB2b ? (
                    <B2b
                      style={{
                        height: 24,
                        position: "relative",
                      }}
                    />
                  ) : (
                    <span
                      style={{ color: hoverEffect ? "#f8f8f8" : "#f2f2f2" }}
                    >
                      empty
                    </span>
                  )}
                </Box>
              </Tooltip>
              <Box>
                {getLocalizedString("Ordine N°")}:{" "}
                <OrderDetailText>{orderNumber}</OrderDetailText>
              </Box>
              <Box>
                {getLocalizedString("ORDER_DRAFT_DATE")}:{" "}
                <OrderDetailText>{orderDateFe || ""}</OrderDetailText>
              </Box>
              <Box>
                {getLocalizedString("Codice")}:{" "}
                <OrderDetailText>{customerCode}</OrderDetailText>
              </Box>
            </Flex>
            <Flex width={1} flexDirection="column" justifyContent="center">
              <Box>
                {getLocalizedString("Tipo")}:{" "}
                <OrderDetailText>{type?.[lang] || ""}</OrderDetailText>
              </Box>
              <Box>
                {getLocalizedString("Linea")}:{" "}
                <OrderDetailText>
                  {lineDescription?.[lang] || ""}
                </OrderDetailText>
              </Box>
              <Box>
                {getLocalizedString("ORDER_DRAFT_BUSINESS_NAME")}:{" "}
                <OrderDetailText>{customerName}</OrderDetailText>
              </Box>
            </Flex>
            <Flex width={1} flexDirection="column" justifyContent="center">
              <Box>
                {getLocalizedString("Stagione")}:{" "}
                <OrderDetailText>{season?.[lang] || ""}</OrderDetailText>
              </Box>
              <Box>
                {getLocalizedString("Stato")}:{" "}
                <OrderDetailText
                  style={{
                    textTransform: "uppercase",
                    color: statusColor,
                  }}
                >
                  {statusLabel}
                </OrderDetailText>
              </Box>
              {customData?.ponumber && (
                <Box pb={10}>
                  {getLocalizedString("PO")}
                  {": "}
                  <OrderDetailText>
                    {customData?.ponumber || "-"}
                  </OrderDetailText>
                </Box>
              )}
            </Flex>
          </Flex>
        </Link>

        <Flex
          width={[1, null, 300]}
          flexDirection="column"
          style={{
            backgroundColor: hoverEffect ? "#404040" : theme.background,
            fontSize: "0.8em",
          }}
          p={3}
          mt={[2, null, 0]}
          ml={[0, null, 2]}
          alignItems="center"
          justifyContent={"center"}
        >
          <Flex
            width={1}
            justifyContent="space-between"
            style={{
              paddingBottom: orderType === typeId ? "20px" : "3px",
            }}
          >
            <Box color={theme.white}>{getLocalizedString("QUANTITA'")}</Box>
            <Box color={theme.white} alignContent="flex-end">
              <strong>{quantityReorder || 0}</strong>
            </Box>
          </Flex>
          <Flex
            width={1}
            justifyContent="space-between"
            style={{
              borderTop: orderType === typeId ? "1px solid #999" : "none",
              paddingTop: orderType === typeId ? "20px" : "2.5px",
              paddingBottom: orderType === typeId ? "0px" : "10px",
            }}
          >
            <Box color={theme.white} sx={{ paddingRight: "1px" }}>
              {getLocalizedString("TOTALE RIORDINO")}
            </Box>
            <Flex color={theme.white} alignContent="flex-end">
              <Price
                price={totalReorder || 0}
                currency={currencyCode}
                style={{
                  fontWeight: 800,
                }}
              />
            </Flex>
          </Flex>
          {orderType != typeId && (
            <Flex
              width={1}
              justifyContent="space-between"
              style={{
                paddingBottom: "3px",
                paddingTop: "10px",
                borderTop: "1px solid #999",
              }}
            >
              <Box color={theme.white}>{getLocalizedString("QUANTITA'")}</Box>
              <Box color={theme.white} alignContent="flex-end">
                <strong>{quantitySwap || 0}</strong>
              </Box>
            </Flex>
          )}
          {orderType != typeId && (
            <Flex
              width={1}
              justifyContent="space-between"
              style={{
                paddingBottom: "0px",
                paddingTop: "2.5px",
              }}
            >
              <Box color={theme.white}>{getLocalizedString("TOTALE RESO")}</Box>
              <Box color={theme.white} alignContent="flex-end">
                <Price
                  price={totalSwap || 0}
                  currency={currencyCode}
                  style={{
                    fontWeight: 800,
                  }}
                />
              </Box>
            </Flex>
          )}
        </Flex>

        {orderStatus != ORDER_STATUS.CANCELLED && (
          <Flex
            flexDirection={["row", null, "column"]}
            backgroundColor={statusColor}
            justifyContent={"space-between"}
            alignItems="center"
            p={["10px", null, "20px"]}
            mt={[2, null, 0]}
            ml={[0, null, 2]}
          >
            <Link
              onClick={(e) => {
                if (orderStatus == ORDER_STATUS.DRAFT) {
                  setDeleteMessage(
                    getLocalizedString("DELETEORDER_MESSAGE") +
                      orderNumber +
                      "?",
                  );
                }
              }}
              style={{
                position: "relative",
                width: "33.3%",
                top: "calc(20% - 26px )",
              }}
              to="#"
            >
              <Box
                sx={{
                  position: "relative",
                  left: "calc(50% - 9px)",
                  width: 0,
                  visibility: hoverEffect ? "" : ["", "hidden", null, "hidden"],
                }}
              >
                {orderStatus == ORDER_STATUS.DRAFT && (
                  <Tooltip content={getLocalizedString("DELETE_ORDER")}>
                    <Box
                      sx={{
                        position: "relative",
                        top: ["2px", null, "2px"],
                        "&:hover > div": {
                          opacity: "1",
                        },
                      }}
                    >
                      <Bin
                        style={{
                          position: "relative",
                          color: "#fff",
                          width: 18,
                          height: 18,
                          zIndex: "1",
                        }}
                      />
                      <Box
                        sx={{
                          width: "28px",
                          position: "absolute",
                          height: "28px",
                          marginTop: "-23px",
                          marginLeft: "-5px",
                          borderRadius: "25px",
                          background: "rgba(0,0,0,.2)",
                          opacity: "0",
                        }}
                      ></Box>
                    </Box>
                  </Tooltip>
                )}
                {status == ORDER_STATUS.DRAFT &&
                  statusDescription == "Cancelled" && (
                    <Box style={{ height: "18px" }}></Box>
                  )}
              </Box>
            </Link>
            <Link
              onClick={(e) => {
                e.stopPropagation();
                if (customerId) {
                  localStorage.setItem("selectedCustomer", customerId);
                }
                if (agentId) {
                  localStorage.setItem("selectedAgent", agentId);
                }
              }}
              to={
                orderStatus == ORDER_STATUS.CONFIRMED
                  ? "/orderReport/" + id
                  : "/orderDetail/" + id
              }
              style={{
                position: "relative",
                width: "33.4%",
                visibility:
                  isB2b ||
                  orderStatus == ORDER_STATUS.CONFIRMED ||
                  orderStatus == ORDER_STATUS.DRAFT
                    ? ""
                    : "hidden",
                pointerEvents: isDisabled ? "none" : "auto",
              }}
            >
              <Tooltip
                content={getLocalizedString(
                  orderStatus == ORDER_STATUS.DRAFT
                    ? "EDIT_ORDER"
                    : "VIEW_ORDER",
                )}
              >
                <Box
                  sx={{
                    position: "relative",
                    left: "calc(50% - 9px)",
                    width: 0,
                    top: ["2px", null, "0px"],
                  }}
                >
                  {orderStatus == ORDER_STATUS.CONFIRMED && (
                    <Box
                      sx={{
                        position: "relative",
                        top: ["2px", null, "8px"],
                        "&:hover > div": {
                          opacity: "1",
                        },
                      }}
                    >
                      {orderStatus == ORDER_STATUS.CONFIRMED && (
                        <SearchPlus
                          style={{
                            marginTop: 5,
                            color: "#fff",
                            width: "32px",
                            position: "relative",
                            left: "-7px",
                            top: "-6px",
                            zIndex: "1",
                            transform: "scale(0.6)",
                            fill: "white",
                          }}
                        />
                      )}
                      {orderStatus != ORDER_STATUS.CONFIRMED && (
                        <Search
                          style={{
                            marginTop: 5,
                            color: "#fff",
                            width: "24px",
                            position: "relative",
                            left: "2px",
                            zIndex: "1",
                          }}
                        />
                      )}

                      <Box
                        sx={{
                          width: "28px",
                          position: "absolute",
                          height: "28px",
                          marginTop: "-35px",
                          marginLeft: "-5px",
                          borderRadius: "25px",
                          background: "rgba(0,0,0,.2)",
                          opacity: "0",
                        }}
                      ></Box>
                    </Box>
                  )}
                  {orderStatus != ORDER_STATUS.CONFIRMED && (
                    <Box
                      sx={{
                        position: "relative",
                        top: ["2px", null, "2px"],
                        "&:hover > div": {
                          opacity: "1",
                        },
                      }}
                    >
                      <Plus
                        style={{
                          color: "#fff",
                          position: "relative",
                          width: 18,
                          height: 18,
                          marginTop: "-5px",
                          zIndex: "1",
                        }}
                      />
                      <Box
                        sx={{
                          width: "28px",
                          position: "absolute",
                          height: "28px",
                          marginTop: "-23px",
                          marginLeft: "-5px",
                          borderRadius: "25px",
                          background: "rgba(0,0,0,.1)",
                          opacity: "0",
                        }}
                      ></Box>
                    </Box>
                  )}
                </Box>
              </Tooltip>
            </Link>
            <Tooltip content={getLocalizedString("PRINT")}>
              <Box
                sx={{
                  position: "relative",
                  left: "calc(50% - 9px)",
                  width: 0,
                  top: ["2px", null, "2px"],
                  visibility: hoverEffect ? "" : ["", "hidden", null, "hidden"],
                  "&:hover > div": {
                    opacity: "1",
                  },
                }}
                onClick={print}
              >
                <Print
                  style={{
                    position: "relative",
                    zIndex: "1",
                    color: "#fff",
                    width: 18,
                    height: 18,
                  }}
                />
                <Box
                  sx={{
                    width: "28px",
                    position: "absolute",
                    height: "28px",
                    marginTop: "-23px",
                    marginLeft: "-5px",
                    borderRadius: "25px",
                    background: "rgba(0,0,0,.1)",
                    opacity: "0",
                  }}
                ></Box>
              </Box>
            </Tooltip>
          </Flex>
        )}

        {orderStatus == ORDER_STATUS.CANCELLED && (
          <Flex
            flexDirection={["row", null, "column"]}
            backgroundColor={statusColor}
            justifyContent={"space-between"}
            p={["10px", null, "20px"]}
            mt={[2, null, 0]}
            ml={[0, null, 2]}
          ></Flex>
        )}
      </Flex>
    </Flex>
  ) : (
    <Flex
      flexDirection="column"
      width={["calc(100% - 20px)", "calc(100% - 20px)", "95%"]}
      mb="20px"
      mx="10px"
    >
      <ToastMessage
        message={deleteMessage}
        needConfirm={true}
        confirmFunction={() => {
          dispatch(yodaOrdersActions.removeOrder(id));
          setDeleteMessage(null);
        }}
        cancelFunction={() => {
          setDeleteMessage(null);
        }}
        style={{ width: 600 }}
      />
      <Flex flexDirection="column" bg="#f4f4f4" px="10px" py="10px">
        <Flex
          onClick={() => setShowExpanded(!showExpanded)}
          width="100%"
          alignItems="center"
          justifyContent={isB2b ? "space-between" : "flex-end"}
        >
          {isB2b ? <B2b style={{ width: "22px", height: "22px" }} /> : null}
          {showExpanded ? <BsChevronDown /> : <BsChevronUp />}
        </Flex>
        <Flex>
          <span style={{ marginRight: "5px" }}>
            {getLocalizedString("ORDER_DRAFT_BUSINESS_NAME")}:{" "}
          </span>
          <span style={{ fontWeight: "bold" }}>{customerName}</span>
        </Flex>
        <Flex>
          <span style={{ marginRight: "5px" }}>
            {getLocalizedString("Ordine N°")}:
          </span>
          <span style={{ fontWeight: "bold" }}>{orderNumber}</span>
        </Flex>
        <Flex>
          <span style={{ marginRight: "5px" }}>
            {getLocalizedString("Tipo")}:
          </span>
          <span style={{ fontWeight: "bold" }}>{type?.[lang] || ""}</span>
        </Flex>

        {showExpanded && (
          <>
            <Flex>
              <span style={{ marginRight: "5px" }}>
                {getLocalizedString("ORDER_DRAFT_DATE")}:
              </span>
              <span style={{ fontWeight: "bold" }}>{orderDateFe || ""}</span>
            </Flex>
            <Flex>
              <span style={{ marginRight: "5px" }}>
                {getLocalizedString("Linea")}:
              </span>
              <span style={{ fontWeight: "bold" }}>
                {lineDescription?.[lang] || ""}
              </span>
            </Flex>
            <Flex>
              <span style={{ marginRight: "5px" }}>
                {getLocalizedString("Stagione")}:
              </span>
              <span style={{ fontWeight: "bold" }}>{season?.[lang] || ""}</span>
            </Flex>
            <Flex>
              <span style={{ marginRight: "5px" }}>
                {getLocalizedString("Stato")}:
              </span>
              <span style={{ fontWeight: "bold", color: statusColor }}>
                {statusLabel}
              </span>
            </Flex>
          </>
        )}
      </Flex>
      <Flex>
        <Flex
          flexDirection="column"
          bg="#232323"
          width="100%"
          p="10px"
          justifyContent="space-between"
        >
          <Flex width="100%" justifyContent="space-between">
            <span style={{ color: "white" }}>
              {getLocalizedString("QUANTITA'")}
            </span>
            <span style={{ color: "white" }}>{totalQuantity}</span>
          </Flex>

          <Flex
            width="100%"
            justifyContent="space-between"
            style={{ borderTop: type.en !== "Swap" && "1px solid white" }}
            pt={type.en !== "Swap" && "22px"}
          >
            <span style={{ color: "white" }}>
              {getLocalizedString("TOTALE RIORDINO")}
            </span>
            <span style={{ color: "white" }}>
              <Price price={totalReorder} currency={currencyCode} />
            </span>
          </Flex>
          {type.en === "Swap" && (
            <Flex
              width="100%"
              justifyContent="space-between"
              style={{ borderTop: "1px solid white" }}
              pt="8px"
            >
              <span style={{ color: "white" }}>
                {getLocalizedString("TOTALE RESO")}
              </span>
              <span style={{ color: "white" }}>
                <Price price={totalSwap} currency={currencyCode} />
              </span>
            </Flex>
          )}
        </Flex>
        <Flex
          flexDirection="column"
          width="10%"
          bg={statusColor}
          sx={{
            minHeight: "116px",
            height: "auto",
          }}
        >
          {orderStatus == ORDER_STATUS.CONFIRMED && (
            <Flex
              height="100%"
              alignItems="center"
              justifyContent="center"
              onClick={() => navigate.push("/orderReport/" + id)}
            >
              {" "}
              <FaSearchPlus color="white" size="20px" />{" "}
            </Flex>
          )}
          {orderStatus == ORDER_STATUS.SUBMITTED && (
            <Flex
              height="100%"
              alignItems="center"
              justifyContent="center"
              onClick={() => navigate.push("/orderDetail/" + id)}
            >
              {" "}
              <FaSearchPlus color="white" size="20px" />{" "}
            </Flex>
          )}
          {orderStatus == ORDER_STATUS.DRAFT && (
            <Flex
              height="100%"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              style={{ gap: "14px" }}
            >
              <BsTrash3
                color="white"
                size="20px"
                onClick={(e) => {
                  setDeleteMessage(
                    getLocalizedString("DELETEORDER_MESSAGE") +
                      orderNumber +
                      "?",
                  );
                }}
              />
              <AiOutlinePlus
                color="white"
                size="20px"
                onClick={() => navigate.push("/orderDetail/" + id)}
              />
              <BsPrinter color="white" size="20px" onClick={print} />
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default OrderDraft;
